/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from 'react';

import ChatIcon from '@mui/icons-material/Chat';
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Tab,
  Tabs,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useParams } from 'react-router-dom';

import ConnectedDraft from './ConnectedDraft';
import DraftAction from './DraftAction';
import { setSelectedText } from './DraftSlice';
import PdfView from './PdfView';
import Sections from './Sections';
import ChatbotComponent from '../../components/Chatbot';
import { a11yProps } from '../../components/CustomTabPanel';
import { riPrimary } from '../../components/Theme/colors';
import {
  useGetConnectedDraftQuery,
  useGetDocumentQuery,
  useGetDraftQuery,
} from '../../redux/services/draftApi';
import { RootState, useAppDispatch, useTypedSelector } from '../../redux/store';

const innerTabStyle = {
  backgroundColor: riPrimary[10],
  borderRadius: '20px',
  paddingX: '15px',
};

const tabs = [
  {
    label: 'Issues',
    value: 'issues',
  },
  {
    label: 'Insights',
    value: 'insights',
  },
  {
    label: 'Clause Library',
    value: 'clause-library',
  },
  { label: 'Connected Drafts', value: 'connected-drafts' },
  { label: 'Action', value: 'action' },
];

const ViewDraft = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState<string>('issues');
  const [showBot, toggleBot] = useState(false);
  const [botFrom, setBotFrom] = useState({
    type: '',
    content: null,
  });

  const dispatch = useAppDispatch();
  const { citation } = useTypedSelector((state: RootState) => state.draft);

  const { data: draft } = useGetDraftQuery(id || '', {
    skip: !id,
  });

  const { data: connectedDrafts } = useGetConnectedDraftQuery(
    `?projects=${draft?.projects?.[0]?.id}&page=1` || '',
    { skip: !draft?.projects?.[0]?.id }
  );

  const activeCitation = useMemo(
    () =>
      connectedDrafts?.results?.find(
        (draft: any) => draft.file_name === citation?.title
      ),
    [citation, connectedDrafts]
  );

  const { data: document, isFetching } = useGetDocumentQuery(
    {
      draft_id: activeCitation?.id || id,
    },
    { refetchOnMountOrArgChange: true }
  );

  const fileLink = useMemo(() => (document as any)?.presigned_url, [document]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    setActiveTab('issues');
  }, [id]);

  const allFiles = useMemo(() => {
    const fileNames =
      connectedDrafts?.results?.map((data: any) => data.file_name) || [];
    const combineFiles = [draft?.file_name, ...fileNames];
    return Array.from(new Set(combineFiles));
  }, [connectedDrafts]);

  return (
    <Box key={id}>
      <Grid container sx={{ mt: 2 }}>
        <Grid size={{ sm: 6 }}>
          {isFetching ? (
            <Box px={2}>
              <CircularProgress />
            </Box>
          ) : (
            <PdfView
              key={`${fileLink}-${activeCitation?.id || id}`}
              fileLink={fileLink}
              draftDetails={activeCitation || draft}
            />
          )}
        </Grid>
        <Grid size={{ sm: 6 }} sx={{ paddingLeft: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="Draft tabs"
            variant="scrollable"
            scrollButtons={false}
            sx={innerTabStyle}
          >
            {tabs.map((tab: any, index: number) => (
              <Tab
                key={index}
                label={tab.label}
                value={tab.value}
                {...a11yProps(tab.value)}
              />
            ))}
          </Tabs>

          {!isFetching && (
            <Stack
              spacing={1}
              sx={{
                height: '80vh',
                overflowY: 'auto',
                backgroundColor: 'riTertiary.50',
                borderRadius: '24px',
                padding: '8px',
                mt: '8px',
              }}
            >
              {activeTab === 'connected-drafts' && <ConnectedDraft />}
              {activeTab === 'action' && <DraftAction />}
              {(activeTab === 'issues' ||
                activeTab === 'clause-library' ||
                activeTab === 'insights') && (
                <Sections
                  activeTab={activeTab}
                  draftId={activeCitation?.id}
                  setBotFrom={({ type, content }: any) => {
                    dispatch(setSelectedText(null));
                    setBotFrom({
                      type: type,
                      content: content,
                    });
                  }}
                  openBoot={() => toggleBot(true)}
                />
              )}
            </Stack>
          )}
        </Grid>
      </Grid>
      <IconButton
        onClick={() => {
          toggleBot((prev) => !prev);
          setBotFrom({
            type: 'global',
            content: null,
          });
        }}
        sx={{
          position: 'absolute',
          bottom: '10%',
          right: '7%',
          color: 'white',
          backgroundColor: '#6D264C',
          '&:hover': {
            color: '#6D264C',
          },
          padding: '16px',
        }}
      >
        <ChatIcon sx={{ fontSize: '30px' }} />
      </IconButton>
      {showBot && draft && (
        <div className="app-chatbot-container">
          <ChatbotComponent
            botFrom={botFrom}
            fileNames={allFiles}
            close={() => toggleBot(false)}
            contractType={draft?.contract_type?.contract_type}
          />
        </div>
      )}
    </Box>
  );
};

export default ViewDraft;
